import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronRight, Clock, MapPin, MessageCircle, RefreshCw } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentService, Appointment } from '@lib/supabase/appointment.service'
import { chatService } from '@lib/supabase/chat.service'
import { providerService, Provider } from '@lib/supabase/provider.service'
import { clsx } from 'clsx'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { queryKeys } from '@lib/queryKeys'

type ChatAppointmentWithProvider = Appointment & { _provider: Provider | null }

const ACTIVE_STATUSES = new Set(['scheduled', 'requested'])
const HISTORY_STATUSES = new Set(['completed', 'cancelled', 'rejected'])
const PAGE_SIZE = 5

function formatTime12h(t?: string | null) {
  if (!t || t === '—' || t === 'TBD') return ''
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

function sortChatAppointments(appointments: ChatAppointmentWithProvider[]) {
  const statusRank: Record<string, number> = {
    scheduled: 0,
    requested: 1,
    completed: 2,
    cancelled: 3,
    rejected: 4,
  }

  return [...appointments].sort((left, right) => {
    const leftStatus = left.status?.toLowerCase() ?? ''
    const rightStatus = right.status?.toLowerCase() ?? ''
    const rankDelta = (statusRank[leftStatus] ?? 99) - (statusRank[rightStatus] ?? 99)
    if (rankDelta !== 0) return rankDelta
    return (right.created_at || '').localeCompare(left.created_at || '')
  })
}

function sortSectionByUnread<T extends { id: string; conversation_id?: string | null }>(
  appointments: T[],
  unreadConversationKeys: Set<string>,
) {
  return [...appointments].sort((left, right) => {
    const leftUnread = unreadConversationKeys.has(chatService.getConversationKey(left.id, left.conversation_id))
    const rightUnread = unreadConversationKeys.has(chatService.getConversationKey(right.id, right.conversation_id))

    if (leftUnread === rightUnread) return 0
    return leftUnread ? -1 : 1
  })
}

function ChatAppointmentCard({
  appointment,
  onOpen,
  hasNewMessage,
}: {
  appointment: ChatAppointmentWithProvider
  onOpen: () => void
  hasNewMessage: boolean
}) {
  const status = appointment.status?.toLowerCase() ?? ''
  const statusStyle: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700',
    requested: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700',
    rejected: 'bg-red-100 text-red-700',
  }

  return (
    <div className="border border-border bg-card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-sky-50 text-sky-600">
          {appointment._provider?.profile_image_url ? (
            <img src={appointment._provider.profile_image_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <MessageCircle size={20} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {appointment._provider?.name ?? 'Healthcare Provider'}
              </p>
              <p className="text-xs text-muted-foreground">
                {appointment._provider?.specialty ?? 'Chat Consultation'}
              </p>
            </div>
            <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium capitalize', statusStyle[status] ?? 'bg-muted text-muted-foreground')}>
              {appointment.status}
            </span>
          </div>

          <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Calendar size={12} />
              {new Date(appointment.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            {appointment.scheduled_time ? (
              <div className="flex items-center gap-2">
                <Clock size={12} />
                {formatTime12h(appointment.scheduled_time)}
              </div>
            ) : null}
            {appointment.location ? (
              <div className="flex items-center gap-2 sm:col-span-2">
                <MapPin size={12} />
                <span className="truncate">{appointment.location}</span>
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">
              Chat Consultation
            </span>
            {hasNewMessage ? (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                New message
              </span>
            ) : null}
            <button
              type="button"
              onClick={onOpen}
              className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Open Chat
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ChatAppointments() {
  const navigate = useNavigate()
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()
  const [visibleRecentCount, setVisibleRecentCount] = useState(PAGE_SIZE)
  const [visibleArchivedCount, setVisibleArchivedCount] = useState(PAGE_SIZE)

  const chatAppointmentsQuery = useQuery({
    queryKey: queryKeys.patient.appointments(patient?.id, undefined, 'Chat Consultation'),
    queryFn: () => appointmentService.getPatientAppointments(undefined, 'Chat Consultation'),
    enabled: !!patient?.id,
  })

  const chatAppointmentsRaw = chatAppointmentsQuery.data ?? []

  const chatProviderIds = useMemo(
    () => Array.from(new Set(chatAppointmentsRaw.map((appointment) => appointment.provider_id).filter(Boolean))).sort(),
    [chatAppointmentsRaw],
  )

  const providerMapQuery = useQuery({
    queryKey: queryKeys.providers.byIds(chatProviderIds),
    queryFn: () => providerService.getProvidersByIds(chatProviderIds),
    enabled: chatProviderIds.length > 0,
  })

  const latestIncomingMapQuery = useQuery({
    queryKey: queryKeys.patient.latestIncomingMessages(patient?.id),
    queryFn: () => chatService.getLatestIncomingProviderMessageMap(patient!.id),
    enabled: !!patient?.id,
  })

  const appointments = useMemo(() => {
    const providerMap = providerMapQuery.data ?? {}
    return sortChatAppointments(
      chatAppointmentsRaw.map((appointment) => ({
        ...appointment,
        _provider: providerMap[appointment.provider_id] ?? null,
      }))
    )
  }, [chatAppointmentsRaw, providerMapQuery.data])

  const unreadConversationKeys = useMemo(() => {
    if (!patient?.id) return new Set<string>()
    return chatService.getUnreadConversationKeys(patient.id, appointments, latestIncomingMapQuery.data ?? {})
  }, [appointments, latestIncomingMapQuery.data, patient?.id])

  const isLoading = chatAppointmentsQuery.isPending || (chatProviderIds.length > 0 && providerMapQuery.isPending)
  const isRefreshing = chatAppointmentsQuery.isRefetching || providerMapQuery.isRefetching || latestIncomingMapQuery.isRefetching
  const error = chatAppointmentsQuery.isError || providerMapQuery.isError || latestIncomingMapQuery.isError
    ? 'Failed to load chat appointments. Please try again.'
    : null

  useEffect(() => {
    if (!patient?.id) return

    const subscription = chatService.subscribeToPatientIncomingMessages(patient.id, () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patient.latestIncomingMessages(patient.id) })
    })

    const handleStorage = (event: StorageEvent) => {
      if (event.key && !event.key.startsWith('patient-chat-seen:')) return
      void queryClient.invalidateQueries({ queryKey: queryKeys.patient.latestIncomingMessages(patient.id) })
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('storage', handleStorage)
    }
  }, [patient?.id, queryClient])

  const handleRefresh = async () => {
    await Promise.all([
      chatAppointmentsQuery.refetch(),
      latestIncomingMapQuery.refetch(),
      providerMapQuery.refetch(),
    ])
  }

  const activeChats = useMemo(
    () => sortSectionByUnread(
      appointments.filter((appointment) => ACTIVE_STATUSES.has(appointment.status?.toLowerCase() ?? '')),
      unreadConversationKeys,
    ),
    [appointments, unreadConversationKeys],
  )

  const recentChats = useMemo(
    () => sortSectionByUnread(
      appointments.filter((appointment) => appointment.status?.toLowerCase() === 'completed'),
      unreadConversationKeys,
    ),
    [appointments, unreadConversationKeys],
  )

  const archivedChats = useMemo(
    () => sortSectionByUnread(
      appointments.filter((appointment) => {
        const status = appointment.status?.toLowerCase() ?? ''
        return HISTORY_STATUSES.has(status) && status !== 'completed'
      }),
      unreadConversationKeys,
    ),
    [appointments, unreadConversationKeys],
  )

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Chat Appointments</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            All your consultation chats in one place so you can reopen them quickly.
          </p>
        </div>
        <button
          title="Refresh chat appointments"
          onClick={() => { void handleRefresh() }}
          disabled={isRefreshing}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <RefreshCw size={18} className={clsx('text-muted-foreground', isRefreshing && 'animate-spin')} />
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="h-40 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
          <button type="button" onClick={() => { void handleRefresh() }} className="mt-3 text-sm font-medium text-primary hover:underline">
            Retry
          </button>
        </div>
      ) : appointments.length === 0 ? (
        <div className="border border-dashed border-border bg-card p-10 text-center">
          <MessageCircle size={34} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-foreground">No chat appointments yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Book a chat consultation and it will appear here for quick access.</p>
          <button
            type="button"
            onClick={() => navigate('/patient/services/Chat%20Consultation')}
            className="mt-4 text-sm font-medium text-primary hover:underline"
          >
            Book a chat consultation
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <section className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Active Chats</h2>
              <p className="text-sm text-muted-foreground">Requested and scheduled consultations that still need your attention.</p>
            </div>
            {activeChats.length > 0 ? (
              <div className="space-y-3">
                {activeChats.map((appointment) => (
                  <ChatAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    hasNewMessage={unreadConversationKeys.has(chatService.getConversationKey(appointment.id, appointment.conversation_id))}
                    onOpen={() => navigate(`/patient/chat/${appointment.id}`, { state: { backPath: '/patient/chats' } })}
                  />
                ))}
              </div>
            ) : (
              <div className="border border-border bg-card p-5 text-sm text-muted-foreground">
                You have no active chat consultations right now.
              </div>
            )}
          </section>

          {recentChats.length > 0 ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Recent Chats</h2>
                <p className="text-sm text-muted-foreground">Reopen recent conversations without hunting through your wider appointment history.</p>
              </div>
              <div className="space-y-3">
                {recentChats.slice(0, visibleRecentCount).map((appointment) => (
                  <ChatAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    hasNewMessage={unreadConversationKeys.has(chatService.getConversationKey(appointment.id, appointment.conversation_id))}
                    onOpen={() => navigate(`/patient/chat/${appointment.id}`, { state: { backPath: '/patient/chats' } })}
                  />
                ))}
              </div>
              {recentChats.length > visibleRecentCount && (
                <button
                  type="button"
                  onClick={() => setVisibleRecentCount((c) => c + PAGE_SIZE)}
                  className="w-full rounded-lg border border-border bg-card py-2.5 text-sm font-medium text-primary hover:bg-muted transition-colors"
                >
                  Show {Math.min(PAGE_SIZE, recentChats.length - visibleRecentCount)} more
                </button>
              )}
            </section>
          ) : null}

          {archivedChats.length > 0 ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Archived Chats</h2>
                <p className="text-sm text-muted-foreground">Past chat consultations that were cancelled or declined.</p>
              </div>
              <div className="space-y-3">
                {archivedChats.slice(0, visibleArchivedCount).map((appointment) => (
                  <ChatAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    hasNewMessage={unreadConversationKeys.has(chatService.getConversationKey(appointment.id, appointment.conversation_id))}
                    onOpen={() => navigate(`/patient/chat/${appointment.id}`, { state: { backPath: '/patient/chats' } })}
                  />
                ))}
              </div>
              {archivedChats.length > visibleArchivedCount && (
                <button
                  type="button"
                  onClick={() => setVisibleArchivedCount((c) => c + PAGE_SIZE)}
                  className="w-full rounded-lg border border-border bg-card py-2.5 text-sm font-medium text-primary hover:bg-muted transition-colors"
                >
                  Show {Math.min(PAGE_SIZE, archivedChats.length - visibleArchivedCount)} more
                </button>
              )}
            </section>
          ) : null}
        </div>
      )}
    </div>
  )
}